import React from 'react';
import { 
  FileX, 
  Users, 
  AlertCircle, 
  Plus, 
  Search,
  Award,
  TrendingUp,
  FileText
} from 'lucide-react';

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ 
  icon, 
  title, 
  description, 
  action,
  className = '' 
}) => {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}>
      {icon && (
        <div className="mb-4 text-gray-500">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-semibold text-gray-300 mb-2">{title}</h3>
      <p className="text-gray-500 mb-6 max-w-md">{description}</p>
      {action && (
        <button
          onClick={action.onClick}
          className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
            action.variant === 'secondary'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

// Specific empty state components for common scenarios

export const NoCertificationsEmpty: React.FC<{ onUpload?: () => void }> = ({ onUpload }) => (
  <EmptyState
    icon={<Award className="w-16 h-16" />}
    title="No Certifications Yet"
    description="Start building your professional profile by uploading your first certification."
    action={onUpload ? {
      label: "Upload Certification",
      onClick: onUpload
    } : undefined}
  />
);

export const NoTeamMembersEmpty: React.FC<{ onAddMember?: () => void }> = ({ onAddMember }) => (
  <EmptyState
    icon={<Users className="w-16 h-16" />}
    title="No Team Members Found"
    description="No technicians match your current filters. Try adjusting your search criteria."
    action={onAddMember ? {
      label: "Add Team Member",
      onClick: onAddMember
    } : undefined}
  />
);

export const NoRevenueDataEmpty: React.FC<{ onUpload?: () => void }> = ({ onUpload }) => (
  <EmptyState
    icon={<TrendingUp className="w-16 h-16" />}
    title="No Revenue Data"
    description="Upload revenue data to track performance and enable leaderboards."
    action={onUpload ? {
      label: "Upload Revenue Data",
      onClick: onUpload
    } : undefined}
  />
);

export const NoSearchResultsEmpty: React.FC<{ onClearFilters?: () => void }> = ({ onClearFilters }) => (
  <EmptyState
    icon={<Search className="w-16 h-16" />}
    title="No Results Found"
    description="No items match your search criteria. Try adjusting your filters or search terms."
    action={onClearFilters ? {
      label: "Clear Filters",
      onClick: onClearFilters,
      variant: 'secondary' as const
    } : undefined}
  />
);

export const NoReportsEmpty: React.FC<{ onCreateReport?: () => void }> = ({ onCreateReport }) => (
  <EmptyState
    icon={<FileText className="w-16 h-16" />}
    title="No Reports Available"
    description="Create your first report to track certification compliance and performance metrics."
    action={onCreateReport ? {
      label: "Create Report",
      onClick: onCreateReport
    } : undefined}
  />
);

export const ErrorState: React.FC<{ onRetry?: () => void; message?: string }> = ({ 
  onRetry, 
  message = "Something went wrong. Please try again." 
}) => (
  <EmptyState
    icon={<AlertCircle className="w-16 h-16 text-red-500" />}
    title="Error"
    description={message}
    action={onRetry ? {
      label: "Try Again",
      onClick: onRetry,
      variant: 'secondary' as const
    } : undefined}
  />
);

export const NoDataEmpty: React.FC<{ title?: string; description?: string }> = ({ 
  title = "No Data Available",
  description = "There is no data to display at this time."
}) => (
  <EmptyState
    icon={<FileX className="w-16 h-16" />}
    title={title}
    description={description}
  />
);

export default EmptyState;
